import { useCallback, useState } from 'react';
import { useStore } from 'react-redux';
import { useIntl } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';
import { NotificationService, NOTIFICATION_TYPE_INFO } from 'ui-components/services/notification-service';
import { emptyObject } from 'ui-components/default-props/empty';

import { createLabelSubject } from '../../../../fulfillment-planner/context';
import { isEmailAddressValid } from '../../../../services/email-sanitation/isEmailAddressValid';
import { sanitizeEmailAddress } from '../../../../services/email-sanitation/sanitizeEmailAddress';
import { buildOrderDetailsCanvasReturnRequest } from '../../../../store/csw/csw.request-builders';
import { createReturnLabelPromise } from '../../../../store/returns/returns.api';
import { getLabelDownloadUrl } from '../../../../store/printing/constants';
import { DocumentService } from '../../../../services/document-service';

import { messages } from './messages';

type UseCreateReturnLabelParams = {
  returnId: string;
  fulfillmentPlanId: string;
  storeGuid: string;
  labelType: string;
  emailAddressCSV: string;
  downloadLabelNow?: boolean;
  onSuccess: (fulfillmentId: string) => void;
  onError?: (errorMessages: string[]) => void;
};

export const useCreateReturnLabel = ({
  returnId,
  fulfillmentPlanId,
  storeGuid,
  labelType,
  emailAddressCSV,
  downloadLabelNow = false,
  onSuccess,
  onError = noop,
}: UseCreateReturnLabelParams) => {
  const { formatMessage } = useIntl();
  const { getState } = useStore();
  const [hasEmailValidationError, setHasEmailValidationError] = useState(false);

  const createReturnLabel = useCallback(() => {
    const state = getState();
    const { returnConfiguration } = buildOrderDetailsCanvasReturnRequest(state, {
      fulfillmentPlanId,
      salesOrderId: '',
      items: [],
      isAutoRMA: false,
      rmaNumber: '',
      note: '',
    });

    const emails = downloadLabelNow ? [] : getEmailsFromCSV(emailAddressCSV);

    if (!downloadLabelNow) {
      const areAllEmailsValid = emails.length > 0 && validateEmailAddresses(emails);
      setHasEmailValidationError(!areAllEmailsValid);
      if (!areAllEmailsValid) {
        return;
      }
    }

    const notificationId = NotificationService.showNotification({
      title: formatMessage(messages.createLabelNotificationTitle),
      content: formatMessage(messages.createLabelNotificationContent, { downloadLabelNow }),
      notificationType: NOTIFICATION_TYPE_INFO,
      dontHide: false,
    });

    setHasEmailValidationError(false);
    createReturnLabelPromise({
      returnId,
      storeGuid,
      shippingConfiguration: returnConfiguration,
      displayScheme: labelType,
      emailTo: emails,
      additionalArgs: {
        downloadLabelNow,
      },
    })
      .then((response) => response?.data || emptyObject)
      .then(({ fulfillmentId }) => {
        NotificationService.removeNotification(notificationId);
        if (downloadLabelNow && fulfillmentId) {
          const url = getLabelDownloadUrl(fulfillmentId);
          DocumentService.downloadFile(url);
        }
        onSuccess(fulfillmentId);
      })
      .catch((errorResponse) => {
        NotificationService.removeNotification(notificationId);
        const errorMessages = errorResponse?.response?.data?.Errors?.map((e: { Message: string }) => e.Message);
        createLabelSubject.next({ fulfillmentPlanId, errorMessages });
        onError(errorMessages);
      });
  }, [returnId, storeGuid, fulfillmentPlanId, emailAddressCSV, downloadLabelNow, labelType, formatMessage, getState, onError, onSuccess]);

  return {
    createReturnLabel,
    hasEmailValidationError,
  };
};

const getEmailsFromCSV = (csv = '') => {
  if (csv.length > 0) {
    return csv.split(',').map((emailAddress) => sanitizeEmailAddress(emailAddress));
  }
  return [];
};

const validateEmailAddresses = (emails: string[]) => {
  return emails.every((emailAddress) => isEmailAddressValid(emailAddress));
};
